import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { useSimulation } from '../../contexts/SimulationContext';
import CONFIG from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingSpinner from '../common/LoadingSpinner';

const SetupWizard = ({ onLaunchSuccess }) => {
    const [step, setStep] = useState(1);
    const { setProject, addAlert, allProjects, setAllProjects } = useSimulation();
    
    // UI State
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [launching, setLaunching] = useState(false);
    
    // Form State
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        start_date: '',
        end_date: '',
        expected_volume: 10000,
        stages: [
            { 
                name: 'HEIGHT', 
                measurement_fields: ['height_cm'], 
                validation_rules: [{ field: 'height_cm', condition: 'min', value: 165 }] 
            }
        ],
        staff_assignments: {
            RUN_OFFICER: [],
            DOCTOR: [],
            STAGE_EVALUATOR: [],
            FIELD_MONITOR: []
        }
    });

    const [ingestedCandidates, setIngestedCandidates] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const resp = await axios.get(`${CONFIG.API_URL}/api/users`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setAllUsers(resp.data);
            } catch (err) {
                console.error('Failed to fetch users:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, []);

    const handleNext = () => {
        if (step === 1 && (!formData.name || !formData.location)) {
            addAlert('Please fill in naming and location details.', 'warning');
            return;
        }
        setStep(step + 1);
    };

    const handleBack = () => setStep(step - 1);

    const handleAddStage = () => {
        setFormData({
            ...formData,
            stages: [...formData.stages, { name: '', measurement_fields: [], validation_rules: [] }]
        });
    };

    const handleRemoveStage = (index) => {
        const newStages = formData.stages.filter((_, i) => i !== index);
        setFormData({ ...formData, stages: newStages });
    };

    const updateStage = (index, field, value) => {
        const newStages = [...formData.stages];
        newStages[index] = { ...newStages[index], [field]: value };
        setFormData({ ...formData, stages: newStages });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            try {
                if (file.name.endsWith('.json')) {
                    const data = JSON.parse(content);
                    const candidates = data.candidates || data;
                    setIngestedCandidates(Array.isArray(candidates) ? candidates : [candidates]);
                    addAlert(`${candidates.length} candidates loaded from JSON.`, 'success');
                } else if (file.name.endsWith('.csv')) {
                    // Simple CSV Parser
                    const lines = content.split('\n');
                    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                    const candidates = lines.slice(1).filter(line => line.trim()).map(line => {
                        const values = line.split(',').map(v => v.trim());
                        const obj = {};
                        headers.forEach((header, i) => {
                            obj[header] = values[i];
                        });
                        return obj;
                    });
                    setIngestedCandidates(candidates);
                    addAlert(`${candidates.length} candidates loaded from CSV.`, 'success');
                } else {
                    addAlert('Unsupported file format. Please use .json or .csv', 'error');
                }
            } catch (err) {
                console.error('File parsing failed:', err);
                addAlert('Failed to parse file. Check format.', 'error');
            }
        };
        reader.readAsText(file);
    };

    const handleLaunch = async () => {
        setLaunching(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            
            // 1. Create Project
            const projectResp = await axios.post(`${CONFIG.API_URL}/api/projects`, formData, config);
            const newProject = projectResp.data;

            // 2. Ingest Candidates if any
            if (ingestedCandidates.length > 0) {
                await axios.post(`${CONFIG.API_URL}/api/candidates/ingest`, {
                    projectId: newProject._id,
                    candidates: ingestedCandidates
                }, config);
            }

            // 3. Update global state
            setAllProjects([newProject, ...allProjects]);
            await setProject(newProject, true);
            
            addAlert(`Drive "${newProject.name}" Launched Successfully!`, 'success');
            
            // Auto-redirect to Live Operations after alert
            if (onLaunchSuccess) {
                setTimeout(() => {
                    onLaunchSuccess();
                }, 1500); 
            }
        } catch (err) {
            console.error('Launch failed:', err);
            const msg = err.response?.data?.message || 'Failed to launch drive.';
            addAlert(msg, 'error');
        } finally {
            setLaunching(false);
        }
    };

    return (
        <div className="main-view animate-fade-in">
            {launching && <LoadingSpinner fullPage message="Deploying Mission Infrastructure..." />}
            {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                    <LoadingSpinner message="Loading Drive Setup..." />
                </div>
            ) : (
                <div className="glass-card" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px' }}>
                {/* Stepper Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '48px', position: 'relative' }}>
                    {[1, 2, 3, 4].map((s) => (
                        <div key={s} style={{ 
                            width: '44px', height: '44px', borderRadius: '14px',
                            background: step >= s ? 'var(--primary)' : 'rgba(0,0,0,0.05)',
                            color: step >= s ? 'white' : 'var(--text-muted)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 800, fontSize: '18px', zIndex: 1, 
                            boxShadow: step === s ? '0 0 20px rgba(128,0,0,0.3)' : 'none',
                            transition: 'all 0.3s ease'
                        }}>
                            {step > s ? <Icon icon="solar:check-read-bold" /> : s}
                        </div>
                    ))}
                    <div style={{ position: 'absolute', top: '22px', left: 0, right: 0, height: '2px', background: 'rgba(0,0,0,0.05)', zIndex: 0 }}></div>
                    <div style={{ 
                        position: 'absolute', top: '22px', left: 0, height: '2px', 
                        background: 'var(--primary)', 
                        width: `${((step - 1) / 3) * 100}%`,
                        zIndex: 0, transition: 'width 0.5s ease'
                    }}></div>
                </div>

                <AnimatePresence mode="wait">
                    {step === 1 && (
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                            <div style={{ marginBottom: '32px' }}>
                                <h2 style={{ fontSize: '28px', fontWeight: 800 }}>Step 1: Drive Initialization</h2>
                                <p style={{ color: 'var(--text-muted)' }}>Define the core identity of the recruitment event.</p>
                            </div>
                            <div style={{ display: 'grid', gap: '24px' }}>
                                <div className="form-group">
                                    <label style={{ fontWeight: 700, marginBottom: '8px', display: 'block' }}>Official Drive Name</label>
                                    <input 
                                        type="text" className="input-field" placeholder="e.g. Bihar Police Recruitment 2026"
                                        value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label style={{ fontWeight: 700, marginBottom: '8px', display: 'block' }}>Physical Location / City</label>
                                    <input 
                                        type="text" className="input-field" placeholder="e.g. Moin-ul-Haq Stadium, Patna"
                                        value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})}
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                                    <div className="form-group">
                                        <label style={{ fontWeight: 700, marginBottom: '8px', display: 'block' }}>Start Date</label>
                                        <input 
                                            type="date" className="input-field"
                                            value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontWeight: 700, marginBottom: '8px', display: 'block' }}>End Date</label>
                                        <input 
                                            type="date" className="input-field"
                                            value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={{ fontWeight: 700, marginBottom: '8px', display: 'block' }}>Expected Candidate Volume (Provisioning)</label>
                                    <input 
                                        type="number" className="input-field"
                                        value={formData.expected_volume} onChange={(e) => setFormData({...formData, expected_volume: e.target.value})}
                                    />
                                    <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>System supports up to 10 Lakh candidates for massive loads.</p>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                            <div style={{ marginBottom: '32px' }}>
                                <h2 style={{ fontSize: '28px', fontWeight: 800 }}>Step 2: Stage Configurator</h2>
                                <p style={{ color: 'var(--text-muted)' }}>Define the physical test rules and measurement points.</p>
                            </div>
                            
                            <div style={{ display: 'grid', gap: '24px' }}>
                                {formData.stages.map((stage, sIdx) => (
                                    <div key={sIdx} className="glass-card" style={{ background: 'rgba(0,0,0,0.02)', padding: '24px', border: '1px solid var(--border-light)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>{sIdx + 1}</div>
                                                <input 
                                                    type="text" placeholder="STAGE NAME (e.g. LONG JUMP)"
                                                    value={stage.name} onChange={(e) => updateStage(sIdx, 'name', e.target.value.toUpperCase())}
                                                    style={{ border: 'none', background: 'none', fontSize: '20px', fontWeight: 800, color: 'var(--text-dark)', outline: 'none', minWidth: '300px' }}
                                                />
                                            </div>
                                            <button onClick={() => handleRemoveStage(sIdx)} style={{ background: 'none', border: 'none', color: '#f43f5e', cursor: 'pointer', padding: '8px' }}>
                                                <Icon icon="solar:trash-bin-trash-bold" width="24" />
                                            </button>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '32px' }}>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: 800, color: 'var(--primary)', letterSpacing: '1px', marginBottom: '12px', display: 'block' }}>MEASUREMENT FIELDS</label>
                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '12px', background: 'white', borderRadius: '14px', border: '1px solid var(--border-light)', minHeight: '52px' }}>
                                                    {stage.measurement_fields.map((f, fIdx) => (
                                                        <span key={fIdx} style={{ background: 'rgba(128,0,0,0.05)', color: 'var(--primary)', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid rgba(128,0,0,0.1)' }}>
                                                            {f}
                                                            <Icon icon="ion:close-circle" style={{ cursor: 'pointer', fontSize: '16px' }} onClick={() => {
                                                                const newFields = stage.measurement_fields.filter((_, i) => i !== fIdx);
                                                                updateStage(sIdx, 'measurement_fields', newFields);
                                                            }} />
                                                        </span>
                                                    ))}
                                                    <input 
                                                        placeholder="+ Field" 
                                                        style={{ border: 'none', outline: 'none', width: '100px', fontSize: '14px', fontWeight: 500 }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                if (e.target.value.trim()) {
                                                                    updateStage(sIdx, 'measurement_fields', [...stage.measurement_fields, e.target.value.trim()]);
                                                                    e.target.value = '';
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: 800, color: 'var(--primary)', letterSpacing: '1px', marginBottom: '12px', display: 'block' }}>VALIDATION RULES</label>
                                                <div style={{ display: 'grid', gap: '8px' }}>
                                                    {stage.validation_rules.map((rule, rIdx) => (
                                                        <div key={rIdx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                            <select 
                                                                className="input-field" style={{ padding: '8px', fontSize: '12px', flex: 1.5 }}
                                                                value={rule.field}
                                                                onChange={(e) => {
                                                                    const newRules = [...stage.validation_rules];
                                                                    newRules[rIdx].field = e.target.value;
                                                                    updateStage(sIdx, 'validation_rules', newRules);
                                                                }}
                                                            >
                                                                <option value="">Select Field</option>
                                                                {stage.measurement_fields.map(f => <option key={f} value={f}>{f}</option>)}
                                                            </select>
                                                            <select 
                                                                className="input-field" style={{ padding: '8px', fontSize: '12px', flex: 1 }}
                                                                value={rule.condition}
                                                                onChange={(e) => {
                                                                    const newRules = [...stage.validation_rules];
                                                                    newRules[rIdx].condition = e.target.value;
                                                                    updateStage(sIdx, 'validation_rules', newRules);
                                                                }}
                                                            >
                                                                <option value="min">min</option>
                                                                <option value="max">max</option>
                                                                <option value="equal">equal</option>
                                                            </select>
                                                            <input 
                                                                type="number" className="input-field" style={{ padding: '8px', fontSize: '12px', flex: 1 }}
                                                                value={rule.value}
                                                                onChange={(e) => {
                                                                    const newRules = [...stage.validation_rules];
                                                                    newRules[rIdx].value = Number(e.target.value);
                                                                    updateStage(sIdx, 'validation_rules', newRules);
                                                                }}
                                                            />
                                                            <button onClick={() => {
                                                                const newRules = stage.validation_rules.filter((_, i) => i !== rIdx);
                                                                updateStage(sIdx, 'validation_rules', newRules);
                                                            }} style={{ background: 'none', border: 'none', color: '#f43f5e', cursor: 'pointer' }}><Icon icon="solar:close-circle-bold" /></button>
                                                        </div>
                                                    ))}
                                                    <button 
                                                        onClick={() => updateStage(sIdx, 'validation_rules', [...stage.validation_rules, { field: stage.measurement_fields[0] || '', condition: 'min', value: 0 }])}
                                                        style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'white', border: '1px dashed var(--primary)', color: 'var(--primary)', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                                    >
                                                        + Add Logic Constraint
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button onClick={handleAddStage} style={{ width: '100%', padding: '20px', borderRadius: '20px', background: 'none', border: '2px dashed var(--border-light)', color: 'var(--text-muted)', fontWeight: 700, fontSize: '14px', cursor: 'pointer', transition: 'all 0.3s ease' }} onMouseOver={(e) => e.target.style.borderColor = 'var(--primary)'} onMouseOut={(e) => e.target.style.borderColor = 'var(--border-light)'}>
                                    <Icon icon="solar:add-circle-bold" style={{ marginRight: '8px' }} />
                                    Add New Physical Test Stage
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                            <div style={{ marginBottom: '32px' }}>
                                <h2 style={{ fontSize: '28px', fontWeight: 800 }}>Step 3: Staff & Role Assignment</h2>
                                <p style={{ color: 'var(--text-muted)' }}>Assign field personnel to their respective roles for this drive.</p>
                            </div>

                            <div style={{ display: 'grid', gap: '32px' }}>
                                {Object.keys(formData.staff_assignments).map(role => (
                                    <div key={role} style={{ 
                                        padding: '24px', borderRadius: '20px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--border-light)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                            <h3 style={{ fontWeight: 800, color: 'var(--primary)', letterSpacing: '0.5px' }}>{role.replace('_', ' ')}</h3>
                                            <span style={{ fontSize: '11px', fontWeight: 800, color: 'white', background: 'var(--primary)', padding: '4px 10px', borderRadius: '20px' }}>
                                                {formData.staff_assignments[role].length} ASSIGNED
                                            </span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                                            {allUsers.filter(u => u.role === role).map(user => {
                                                const isSelected = formData.staff_assignments[role].includes(user._id);
                                                return (
                                                    <div 
                                                        key={user._id} 
                                                        onClick={() => {
                                                            const current = formData.staff_assignments[role];
                                                            const updated = isSelected 
                                                                ? current.filter(id => id !== user._id)
                                                                : [...current, user._id];
                                                            setFormData({ ...formData, staff_assignments: { ...formData.staff_assignments, [role]: updated } });
                                                        }}
                                                        style={{ 
                                                            padding: '16px', borderRadius: '16px', border: '1px solid',
                                                            background: isSelected ? 'var(--primary)' : 'white',
                                                            borderColor: isSelected ? 'var(--primary)' : 'var(--border-light)',
                                                            color: isSelected ? 'white' : 'var(--text-dark)',
                                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: isSelected ? '0 8px 15px rgba(128,0,0,0.2)' : 'none'
                                                        }}
                                                    >
                                                        <div style={{ 
                                                            width: '32px', height: '32px', borderRadius: '10px', 
                                                            background: isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800
                                                        }}>
                                                            {user.full_name?.charAt(0)}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 700, fontSize: '13px' }}>{user.full_name}</div>
                                                            <div style={{ fontSize: '10px', opacity: isSelected ? 0.8 : 0.6 }}>{user.description || user.designation || 'Staff'}</div>
                                                        </div>
                                                        {isSelected && <Icon icon="solar:check-circle-bold" width="20" />}
                                                    </div>
                                                );
                                            })}
                                            {allUsers.filter(u => u.role === role).length === 0 && (
                                                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px', border: '1px dashed var(--border-light)', borderRadius: '16px' }}>
                                                    No {role.toLowerCase().replace('_', ' ')}s available. Add them in Staff Management.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {step === 4 && (
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                            <div style={{ marginBottom: '32px' }}>
                                <h2 style={{ fontSize: '28px', fontWeight: 800 }}>Step 4: Data Ingestion & Launch</h2>
                                <p style={{ color: 'var(--text-muted)' }}>Finalize the setup and upload initial roster.</p>
                            </div>

                            <div 
                                style={{ 
                                    border: '2px dashed var(--primary)', borderRadius: '24px', padding: '48px', 
                                    textAlign: 'center', background: 'rgba(128,0,0,0.02)', marginBottom: '32px' 
                                }}
                                onClick={() => document.getElementById('roster-upload').click()}
                            >
                                <Icon icon="solar:cloud-upload-bold-duotone" width="64" height="64" color="var(--primary)" />
                                <h3 style={{ marginTop: '16px', fontWeight: 800 }}>Upload Candidate Master Roster</h3>
                                <p style={{ color: 'var(--text-muted)' }}>Support for .json and .csv files</p>
                                <input id="roster-upload" type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                                {ingestedCandidates.length > 0 && (
                                    <div style={{ marginTop: '16px', padding: '8px 16px', background: '#10b981', color: 'white', borderRadius: '20px', display: 'inline-block', fontWeight: 800 }}>
                                        {ingestedCandidates.length} Candidates Loaded!
                                    </div>
                                )}
                            </div>

                            <div className="glass-card" style={{ background: '#f8fafc', padding: '24px' }}>
                                <h4 style={{ fontWeight: 800, marginBottom: '16px' }}>Configuration Summary</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px' }}>
                                    <div><span style={{ color: 'var(--text-muted)' }}>Drive:</span> <strong>{formData.name}</strong></div>
                                    <div><span style={{ color: 'var(--text-muted)' }}>Location:</span> <strong>{formData.location}</strong></div>
                                    <div><span style={{ color: 'var(--text-muted)' }}>Stages:</span> <strong>{formData.stages.length} Configured</strong></div>
                                    <div><span style={{ color: 'var(--text-muted)' }}>Staff Assigned:</span> <strong>{Object.values(formData.staff_assignments).flat().length} Members</strong></div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Footer Actions */}
                <div style={{ marginTop: '48px', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-light)', paddingTop: '32px' }}>
                    {step > 1 ? (
                        <button className="btn-secondary" onClick={handleBack} style={{ width: 'auto', padding: '12px 32px' }}>Previous Step</button>
                    ) : (
                        <div></div>
                    )}
                    
                    {step < 4 ? (
                        <button className="btn-primary" onClick={handleNext} style={{ width: 'auto', padding: '12px 48px' }}>Continue</button>
                    ) : (
                        <button className="btn-primary" onClick={handleLaunch} style={{ width: 'auto', padding: '12px 64px', background: '#10b981' }}>LAUNCH DRIVE</button>
                    )}
                </div>
            </div>
        )}
    </div>
);
};

export default SetupWizard;
